import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  useCopyOverride,
  usePromoActive,
  isPromoPreview,
  safeColor,
  COPY_DEFAULTS,
} from "@/lib/copyOverrides";
import { sendPromoEvent, sendPromoViewOnce } from "@/lib/promoEvents";

// Inline announcement banner — renders inside the sticky header.
// Pulls copy from Supabase (site_copy_overrides) with realtime updates.
const PromoBanner = () => {
  const location = useLocation();

  const hideBanner =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/images5");

  const desktopCopy = useCopyOverride("banner.desktop", COPY_DEFAULTS["banner.desktop"]);
  const mobileCopy  = useCopyOverride("banner.mobile",  COPY_DEFAULTS["banner.mobile"]);
  const href        = useCopyOverride("banner.href",    COPY_DEFAULTS["banner.href"]);
  const bg          = useCopyOverride("banner.bg",      COPY_DEFAULTS["banner.bg"]);
  const fg          = useCopyOverride("banner.fg",      COPY_DEFAULTS["banner.fg"]);
  const active      = usePromoActive("banner");
  const preview     = isPromoPreview("banner");

  const visible = !hideBanner && (active || preview);

  useEffect(() => {
    if (visible) sendPromoViewOnce("banner", location.pathname);
  }, [visible, location.pathname]);

  if (!visible) return null;

  const bgColor = safeColor(bg);
  const fgColor = safeColor(fg);
  const style: React.CSSProperties = {};
  if (bgColor) style.backgroundColor = bgColor;
  if (fgColor) style.color = fgColor;

  const isExternal = /^https?:\/\//i.test(href);
  const className = [
    "block text-center py-2 px-4 text-sm font-medium hover:opacity-90 transition-opacity",
    bgColor ? "" : "bg-gradient-ocean",
    fgColor ? "" : "text-primary-foreground",
  ]
    .filter(Boolean)
    .join(" ");

  const onClick = () => sendPromoEvent("banner", "click");

  const inner = (
    <>
      <span className="hidden sm:inline whitespace-pre-wrap">{desktopCopy}</span>
      <span className="sm:hidden whitespace-pre-wrap">{mobileCopy}</span>
    </>
  );

  return isExternal ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} style={style} onClick={onClick}>{inner}</a>
  ) : (
    <Link to={href} className={className} style={style} onClick={onClick}>{inner}</Link>
  );
};

export default PromoBanner;
